import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const PURPLE = '#403EC9';

const SEVERITY_CONFIG = {
  critical: { bg:'#FEE2E2', color:'#DC2626', border:'#FCA5A5', icon:'🔴', label:'Critical' },
  warning:  { bg:'#FEF3C7', color:'#D97706', border:'#FCD34D', icon:'🟡', label:'Warning'  },
  info:     { bg:'#EFF6FF', color:'#2563EB', border:'#93C5FD', icon:'🔵', label:'Info'     },
  positive: { bg:'#DCFCE7', color:'#16A34A', border:'#86EFAC', icon:'🟢', label:'Good'     },
};

const timeAgo = (dt) => {
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
};

export default function NotificationBell() {
  const navigate                = useNavigate();
  const [open, setOpen]         = useState(false);
  const [notifs, setNotifs]     = useState([]);
  const [unread, setUnread]     = useState(0);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState('all');
  const drawerRef               = useRef(null);
  const pollRef                 = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setUnread(r.data.unread || 0);
    } catch {}
  }, []);

  const fetchNotifs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications', { params:{ limit:60 } });
      setNotifs(r.data.data || []);
      setUnread(r.data.unread || 0);
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Poll unread count every 2 min
  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, 2 * 60 * 1000);
    return () => clearInterval(pollRef.current);
  }, [fetchUnread]);

  // Fetch full list when drawer opens
  useEffect(() => {
    if (open) fetchNotifs();
  }, [open, fetchNotifs]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifs(n => n.map(x => ({ ...x, is_read:true })));
      setUnread(0);
    } catch {}
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear all ${notifs.length} notifications? This cannot be undone.`)) return;
    try {
      await api.delete('/notifications/clear-all');
      setNotifs([]);
      setUnread(0);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifs(n => n.map(x => x.id===id ? { ...x, is_read:true } : x));
      setUnread(u => Math.max(0, u-1));
    } catch {}
  };

  const runEngine = async () => {
    try {
      const r = await api.post('/notifications/run-engine');
      alert(r.data.message);
      fetchNotifs();
    } catch(e) { alert(e?.response?.data?.message || 'Failed'); }
  };

  const filtered = notifs.filter(n => {
    if (filter === 'unread')   return !n.is_read;
    if (filter === 'critical') return n.severity === 'critical';
    if (filter === 'warning')  return n.severity === 'warning';
    return true;
  });

  const criticalCount = notifs.filter(n => !n.is_read && n.severity === 'critical').length;

  return (
    <div ref={drawerRef} style={{ position:'relative' }}>

      {/* Bell button */}
      <button onClick={()=>setOpen(o=>!o)}
        style={{ position:'relative', background:'none', border:'none', cursor:'pointer', padding:'6px 8px', borderRadius:8,
          background: open ? '#EEEDFE' : 'transparent',
          color: open ? PURPLE : 'var(--text-secondary)' }}>
        <span style={{ fontSize:18 }}>🔔</span>
        {unread > 0 && (
          <span style={{ position:'absolute', top:2, right:2, minWidth:16, height:16, borderRadius:99,
            background: criticalCount > 0 ? '#DC2626' : PURPLE,
            color:'#fff', fontSize:'0.6rem', fontWeight:700,
            display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div style={{ position:'fixed', top:0, right:0, width:380, height:'100vh', background:'var(--bg-card)',
          borderLeft:'1px solid var(--border)', boxShadow:'-8px 0 32px rgba(0,0,0,0.12)',
          zIndex:500, display:'flex', flexDirection:'column', overflowY: 'hidden' }}>

          {/* Header */}
          <div style={{ padding:'16px 18px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.95rem' }}>Notifications</div>
                {unread > 0 && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:1 }}>{unread} unread</div>}
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                {unread > 0 && (
                  <button onClick={markAllRead}
                    style={{ fontSize:'0.72rem', padding:'3px 9px', borderRadius:6, border:'1px solid var(--border)', background:'none', cursor:'pointer', color:'var(--text-secondary)' }}>
                    Mark all read
                  </button>
                )}
                {notifs.length > 0 && (
                  <button onClick={clearAll}
                    style={{ fontSize:'0.72rem', padding:'3px 9px', borderRadius:6, border:'1px solid #FCA5A5', background:'none', cursor:'pointer', color:'#DC2626' }}>
                    Clear all
                  </button>
                )}
                <button onClick={()=>setOpen(false)}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'var(--text-muted)', padding:'2px 6px' }}>✕</button>
              </div>
            </div>

            {/* Filter tabs */}
            <div style={{ display:'flex', gap:4 }}>
              {[
                { id:'all',      label:'All' },
                { id:'unread',   label:`Unread${unread>0?` (${unread})`:''}` },
                { id:'critical', label:'Critical' },
                { id:'warning',  label:'Warnings' },
              ].map(f=>(
                <button key={f.id} onClick={()=>setFilter(f.id)}
                  style={{ padding:'4px 10px', borderRadius:6, fontSize:'0.72rem', cursor:'pointer', border:'none', fontWeight:filter===f.id?600:400,
                    background: filter===f.id ? PURPLE : 'var(--bg)',
                    color: filter===f.id ? '#fff' : 'var(--text-secondary)' }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notification list */}
          <div style={{ flex:1, overflowY:'auto' }}>
            {loading && (
              <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:48 }}>
                <div style={{ fontSize:36, marginBottom:8 }}>🎉</div>
                <div style={{ fontWeight:600, fontSize:'0.88rem' }}>All clear!</div>
                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:4 }}>No {filter !== 'all' ? filter + ' ' : ''}notifications</div>
              </div>
            )}
            {!loading && filtered.map((n, i) => {
              const cfg = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.info;
              return (
                <div key={n.id || i}
                  onClick={()=>{
                    if(!n.is_read) markRead(n.id);
                    if(n.related_table === 'tasks' && n.related_id) {
                      setOpen(false);
                      navigate(`/tasks?task=${n.related_id}`);
                    }
                  }}
                  style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', cursor: (n.related_table === 'tasks' && n.related_id) || !n.is_read ? 'pointer' : 'default',
                    background: n.is_read ? 'transparent' : `${cfg.bg}66`,
                    transition:'background 0.2s' }}>
                  <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    {/* Severity dot */}
                    <div style={{ width:8, height:8, borderRadius:'50%', background:cfg.color, marginTop:5, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                        <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize:'0.85rem', lineHeight:1.4, color:'var(--text-primary)' }}>
                          {n.title}
                        </div>
                        {!n.is_read && (
                          <div style={{ width:7, height:7, borderRadius:'50%', background:PURPLE, flexShrink:0, marginTop:4 }} />
                        )}
                      </div>
                      {n.message && (
                        <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:3, lineHeight:1.5 }}>
                          {n.message}
                        </div>
                      )}
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:5 }}>
                        <span style={{ fontSize:'0.65rem', padding:'1px 6px', borderRadius:99, fontWeight:500,
                          background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}` }}>
                          {cfg.icon} {cfg.label}
                        </span>
                        <span style={{ fontSize:'0.65rem', color:'var(--text-muted)' }}>
                          {timeAgo(n.triggered_at)}
                        </span>
                        {n.entity_type && n.entity_type !== 'user' && (
                          <span style={{ fontSize:'0.65rem', color:'var(--text-muted)', textTransform:'capitalize' }}>
                            · {n.entity_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding:'12px 16px', borderTop:'1px solid var(--border)', flexShrink:0, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Updates every 30 min</div>
            <button onClick={runEngine}
              style={{ fontSize:'0.72rem', padding:'4px 10px', borderRadius:6, border:`1px solid ${PURPLE}`, background:'none', cursor:'pointer', color:PURPLE, fontWeight:500 }}>
              ⚡ Run now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
